import { useEffect, useId, useState } from 'react'

import { faBookmark } from '@fortawesome/pro-regular-svg-icons'
import { faBookmark as faBookmarkSolid } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

import { useDeviceInfo } from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  DeviceType,
  Breakpoint,
  AnalyticsEntityType,
  AnalyticsSource,
  Button as ReactButton,
  DropdownWithoutLabel,
  DropdownWithoutLabelSize,
  FormattedMessage,
} from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Colors, Typography } from '@b-stock/fe-core/design-system'

import { useListingSearch } from '@/components/listings/search/ListingSearchProvider'
import { FeatureFlags } from '@/helpers/featureFlags'
import { PermissionGranule, usePermission } from '@/helpers/permissions'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import { useSavedSearch } from './SavedSearchContext'
import SavedSearchModal from './SavedSearchModal'

const Container = styled.div`
  display: flex;
  justify-content: start;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
  max-width: 11.75rem;

  button {
    justify-content: flex-start;
  }

  @media ${Breakpoints.max.medium} {
    ${Typography.Subtitle3}
    color: ${Colors.Semantic.BStock.default.lightBackground};
    padding: 0;
    border-top: none;
    text-align: center;
    max-width: none;
  }
`

const Heading = styled.h4`
  ${Typography.Subtitle2}
  display: block;
  color: ${Colors.Semantic.Neutral.black};
`

const CurrentSearch = styled.div`
  display: flex;
  gap: 0.75rem;
`

const CurrentSearchResults = styled.div`
  @media ${Breakpoints.min.large} {
    width: 8.5rem;
  }
  p {
    ${Typography.Body3}
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const Button = styled(ReactButton).attrs({
  appearance: 'plainTertiary',
  size: 'small',
  type: 'button',
})``

const SwitchButton = styled(Button)`
  font-size: 0.75rem;
`

const SearchDropdown = styled(DropdownWithoutLabel)`
  width: 100%;
  > span {
    min-width: 0;
  }
` as typeof DropdownWithoutLabel

const EmptySearchPlaceholder = styled.span`
  display: inline-flex;
  align-items: center;
  width: 100%;
  border: 0.0625rem solid ${Colors.Semantic.Neutral.medium_grey};
  background-color: ${Colors.Semantic.Neutral.light_grey};
  color: ${Colors.Semantic.Neutral.dark_grey};
  border-radius: 0.25rem;
  padding: 0.3125rem 1rem;
  ${Typography.Body2}
`

const SeeAllButton = styled(Button)`
  font-size: 0.75rem;
`

const MobileSavedButton = styled(ReactButton).attrs({
  appearance: 'secondary',
  size: 'small',
  type: 'button',
})`
  align-items: center;
  display: flex;
  font-size: 0.75rem;
  gap: 0.5rem;
  line-height: 1rem;
`

const MobileIcon = styled(FontAwesomeIcon)`
  font-size: 0.75rem;
`

const HeadingWrapper = styled.div`
  position: relative;
`

const AddedTooltip = styled.div`
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 0;
  background-color: ${Colors.Semantic.Success.highlight};
  color: ${Colors.Semantic.Neutral.white};
  border-radius: 0.25rem;
  padding: 0.375rem 0.75rem;
  ${Typography.Body3}
  white-space: nowrap;
  pointer-events: none;
  z-index: 10;

  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 1rem;
    border: 0.375rem solid transparent;
    border-bottom-color: ${Colors.Semantic.Success.highlight};
  }
`

const Badge = styled.span`
  align-items: center;
  background-color: ${Colors.Semantic.BStock.default.lightBackground};
  border-radius: 0.625rem;
  color: ${Colors.Semantic.Neutral.white};
  display: inline-flex;
  justify-content: center;
  min-width: 1.25rem;
  padding: 0 0.375rem;
  ${Typography.Subtitle3}
`

const SavedSearchButton = ({
  buttonOnly = false,
}: {
  buttonOnly?: boolean
}) => {
  const [openModal, setOpenModal] = useState(false)
  const justSavedTooltipId = useId()
  const intl = useIntl()

  const {
    currentUsersCurrentSavedSearchName,
    hasValidCurrentUserSavedSearch,
    isFetching,
    justSaved,
    savedSearchesCount,
    savedSearches,
    savedSearchId,
  } = useSavedSearch()

  const { setSavedSearch } = useListingSearch()
  const { trackButtonClicked } = useAnalytics()
  const { deviceType, breakpoint } = useDeviceInfo()
  const isSavedSearchImprovementsEnabled = useFlag(
    FeatureFlags.ENABLE_SAVED_SEARCH_UX_IMPROVEMENTS
  )
  const canReadSavedSearches = usePermission(
    PermissionGranule.FEBP_SAVED_SEARCHES_READ
  )

  useEffect(() => {
    if (!isSavedSearchImprovementsEnabled && savedSearchesCount === 0) {
      setOpenModal(false) // oxlint-disable-line react/set-state-in-effect
    }
  }, [savedSearchesCount, isSavedSearchImprovementsEnabled])

  if (!openModal && isFetching) return null
  if (!canReadSavedSearches) return null
  if (
    !isSavedSearchImprovementsEnabled &&
    !openModal &&
    savedSearchesCount === 0
  )
    return null

  const isMobile = breakpoint < Breakpoint.LARGE
  const isPhone =
    breakpoint < Breakpoint.MEDIUM || deviceType === DeviceType.PHONE

  const toggleModal = () => setOpenModal((prev) => !prev)

  enum ButtonName {
    APPLY_SAVED_SEARCH_DROPDOWN = 'apply_saved_search_dropdown',
  }

  const applySavedSearchFromDropdown = (
    id: Parameters<typeof setSavedSearch>[0]
  ) => {
    trackButtonClicked({
      screenName: 'all_auction',
      buttonName: ButtonName.APPLY_SAVED_SEARCH_DROPDOWN,
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
      saved_search_id: id,
    })
    setSavedSearch(id)
  }

  const headingLabelId = 'shoppingList'
  const buttonLabelId = isPhone ? 'shoppingSrch' : 'shoppingLists'

  return (
    <>
      <Container>
        {!isMobile && !buttonOnly && isSavedSearchImprovementsEnabled ? (
          <>
            <HeadingWrapper>
              <Heading
                aria-describedby={justSaved ? justSavedTooltipId : undefined}
              >
                <FormattedMessage id={`SavedSearch.${headingLabelId}`} />
              </Heading>
              {justSaved && (
                <AddedTooltip
                  id={justSavedTooltipId}
                  role="tooltip"
                  aria-live="polite"
                >
                  <FormattedMessage id="SavedSearch.addedToSavedSearchAlerts" />
                </AddedTooltip>
              )}
            </HeadingWrapper>
            {savedSearchesCount === 0 ? (
              <EmptySearchPlaceholder>
                <FormattedMessage id="SavedSearch.sidebarEmptyState" />
              </EmptySearchPlaceholder>
            ) : (
              <SearchDropdown
                placeholder={intl.formatMessage({
                  id: 'SavedSearch.selectSearch',
                })}
                options={savedSearches.map((ss) => ({
                  value: ss._id,
                  label: ss.name,
                }))}
                value={savedSearchId}
                onChange={(id) => applySavedSearchFromDropdown(id)}
                size={DropdownWithoutLabelSize.narrow}
              />
            )}
            <SeeAllButton onClick={toggleModal}>
              <FormattedMessage id="SavedSearch.seeAllSavedSearchAlerts" />
            </SeeAllButton>
          </>
        ) : !isMobile && !buttonOnly && currentUsersCurrentSavedSearchName ? (
          <>
            <Heading>
              <FormattedMessage id={`SavedSearch.${headingLabelId}`} />
            </Heading>
            <CurrentSearch>
              <CurrentSearchResults>
                <p title={currentUsersCurrentSavedSearchName}>
                  {currentUsersCurrentSavedSearchName}
                </p>
              </CurrentSearchResults>
              <SwitchButton onClick={toggleModal}>
                <FormattedMessage id="SavedSearch.switch" />
              </SwitchButton>
            </CurrentSearch>
          </>
        ) : !buttonOnly && isMobile && isSavedSearchImprovementsEnabled ? (
          <MobileSavedButton onClick={toggleModal}>
            <MobileIcon
              icon={
                hasValidCurrentUserSavedSearch ? faBookmarkSolid : faBookmark
              }
            />
            <FormattedMessage id="SavedSearch.saved" />
            <Badge>{savedSearchesCount}</Badge>
          </MobileSavedButton>
        ) : (
          <Button onClick={toggleModal}>
            <FormattedMessage
              id={`SavedSearch.${buttonLabelId}`}
              values={{ count: savedSearchesCount }}
            />
          </Button>
        )}
      </Container>

      {openModal && (
        <SavedSearchModal isMobile={isMobile} closeModal={toggleModal} />
      )}
    </>
  )
}

export default SavedSearchButton
